/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Button } from 'primereact/button';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
  onLogout?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundaryComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    window.location.hash = '#/';
  };

  handleReload = (): void => {
    const { onLogout } = this.props;
    if (onLogout) {
      onLogout();
    }
    window.location.reload();
  };

  render(): ReactNode {
    const { t } = this.props;

    if (this.state.hasError) {
      return (
        <div className="flex align-items-center justify-content-center w-full h-screen surface-ground">
          <div className="surface-card p-5 border-round shadow-2 w-full w-5">
            <div className="text-center mb-4">
              <i className="pi pi-exclamation-triangle text-6xl text-red-500 mb-3"></i>
              <h1 className="text-3xl font-bold text-900 mb-2">{t('error_boundary.title')}</h1>
              <p className="text-600 mb-4">{t('error_boundary.description')}</p>
            </div>

            <div className="surface-50 border-round p-3 mb-4">
              <h3 className="text-lg font-semibold text-900 mb-2">{t('error_boundary.error_details')}</h3>
              <p className="text-sm text-red-600 mb-2 font-mono break-all">
                {this.state.error?.message || t('error_boundary.unknown_error')}
              </p>
              {this.state.error?.stack && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-600 hover:text-900">
                    {t(['error_boundary.view_stack_trace', 'error_boundary.view_stack_trace'])}
                  </summary>
                  <pre className="text-xs text-700 mt-2 overflow-auto max-h-20rem p-2 bg-white border-round">
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </div>

            <div className="flex gap-2 justify-content-center">
              <Button
                icon="pi pi-replay"
                onClick={this.handleReload}
                label={t(['error_boundary.reload_page', 'Reload page'])}
              />
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ErrorBoundaryWithTranslation = withTranslation()(ErrorBoundaryComponent);

// Re-export with the original name
export { ErrorBoundaryWithTranslation as ErrorBoundary };
